import type { ApiError } from "../shared/api/client";

/**
 * Every way the Mini App can fail to show a screen, each with its own copy
 * and way out. A bare error message never reaches the owner.
 */
export type RecoveryKind =
  | "host_unsupported"
  | "session_rejected"
  | "auth_expired"
  | "nav_token_expired"
  | "offline"
  | "backend_error"
  | "render_error";

/** A startup failure whose kind is already known where it is thrown. */
export class RecoveryError extends Error {
  constructor(readonly kind: RecoveryKind) {
    super(kind);
  }
}

function apiStatus(cause: unknown): number | undefined {
  if (!(cause instanceof Error)) return undefined;
  const detail = cause.cause as Partial<ApiError> | undefined;
  return typeof detail?.status === "number" ? detail.status : undefined;
}

export function recoveryFor(
  cause: unknown,
  online: boolean = navigator.onLine,
): RecoveryKind {
  if (cause instanceof RecoveryError) return cause.kind;
  // fetch rejects with a TypeError when the request never reached a server.
  if (!online || cause instanceof TypeError) return "offline";
  if (apiStatus(cause) === 401) return "auth_expired";
  return "backend_error";
}

/** Only a mid-session sign-out interrupts a loaded screen; other failures stay inline. */
export function isSessionLoss(cause: unknown): boolean {
  return apiStatus(cause) === 401;
}

/** The workspace awaits purge: the server refuses data calls to a restricted session. */
export function isPendingDeletion(cause: unknown): boolean {
  if (!(cause instanceof Error)) return false;
  const detail = cause.cause as Partial<ApiError> | undefined;
  return detail?.status === 403 && detail.code === "account_pending_deletion";
}

/**
 * Outside Telegram the app has no signed identity. A developer machine still
 * gets the local shell; any other origin is told to open the bot instead.
 */
export function runsLocally(hostname: string = window.location.hostname) {
  return ["localhost", "127.0.0.1", "[::1]"].includes(hostname);
}

/** Where "Open in Telegram" goes; absent until the build names the bot. */
export function botLink(): string | undefined {
  const username: unknown = import.meta.env.VITE_TELEGRAM_BOT_USERNAME;
  return typeof username === "string" && /^\w{5,32}$/.test(username)
    ? `https://t.me/${username}`
    : undefined;
}
